/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import { isBlank } from '@ember/utils';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import FormValidations from 'cio/utils/form-validations';
import { COLOR_TEXT } from 'cio/utils/colors';
import AccountAddressValidations, { AccountAddressFormValues } from 'cio/utils/validations/account-address';
import StripeV3Service from 'ember-stripe-elements/services/stripev3';

import CouponModel from 'cio/models/coupon';
import AccountService from 'cio/services/account';
import BillingService from 'cio/services/billing';
import moment from 'moment-timezone';
import { tracked } from '@glimmer/tracking';
import MasqService from 'cio/services/masq';
import AlertsService from 'cio/services/alerts';
import { Task } from 'ember-concurrency';

const Validations = {
  ...AccountAddressValidations,
  name(value: string) {
    if (!value || isBlank(value.trim())) {
      return "Name can't be blank";
    }

    return true;
  },
};

interface CreditCardFormValues extends AccountAddressFormValues {
  name: string;
}

interface CreditCardFormArgs {
  submitAction: ({ setupIntent }: { setupIntent: string | undefined }) => unknown;
  cancelAction?: () => unknown;
  couponCode?: string;
  coupon?: CouponModel;
  formLabel: string | null;
  cancelActionLabel: string | null;
  buttonText: string;
  getUpdatedTaxRateTask: Task<Promise<void>, [{ country: string; state: string; zipCode: string }, boolean]>;
  canCancel?: boolean;
  showCouponAlert?: boolean;
  showTotalDue?: boolean;
  total: number;
  discount: number;
}

export default class CreditCardForm extends Component<CreditCardFormArgs> {
  @service account!: AccountService;
  @service alerts!: AlertsService;
  @service billing!: BillingService;
  @service masq!: MasqService;
  @service('stripev3') stripe!: StripeV3Service;

  @tracked formValues: CreditCardFormValues;
  @tracked stripeError: any = null;

  stripeCardNumOptions = {
    style: {
      base: {
        color: COLOR_TEXT,
      },
    },
    classes: {
      base: 'fly-form-control ember-stripe-card',
      focus: 'is-focused',
      invalid: 'is-error',
    },
    showIcon: true,
  };
  stripeCardDetailOptions = {
    style: {
      base: {
        color: COLOR_TEXT,
      },
    },
    classes: {
      base: 'fly-form-control ember-stripe-card',
      focus: 'is-focused',
      invalid: 'is-error',
    },
  };

  @tracked validations!: FormValidations;

  constructor(owner: unknown, args: CreditCardFormArgs) {
    super(owner, args);

    this.formValues = {
      name: '',
      addressLine1: this.account.current.addressLine1,
      addressLine2: this.account.current.addressLine2,
      city: this.account.current.city,
      zipCode: this.account.current.zipCode,
      country: this.account.current.country,
      state: this.account.current.state,
    };

    this.validations = FormValidations.create({ validations: Validations, context: this, modelKey: 'formValues' });
    this.stripe.load();
  }

  get showSubmitTooltip(): boolean {
    return Boolean(this.validations.hasErrors || this.stripeError != null || !this.allStripeElementsComplete);
  }

  get submitTooltipMessage(): string {
    if (this.validations.errorsList[0] != null) {
      return this.validations.errorsList[0][0];
    }
    if (this.stripeError?.message != null) {
      return this.stripeError.message;
    }
    if (!this.allStripeElementsComplete) {
      return 'Your card details are incomplete.';
    }
    return '';
  }

  get isMonthlyInterval() {
    return (this.billing.pinnedPlan?.intervalName === 'month' ?? false) || this.billing.essentialsPlan;
  }

  get submitDisabled(): boolean {
    return (
      this.masq.readOnly ||
      this.validations.hasErrors ||
      !this.allStripeElementsComplete ||
      this.submitTask.isRunning ||
      (this.args.getUpdatedTaxRateTask?.isRunning ?? false)
    );
  }

  get formLabelDefault() {
    return this.args.formLabel ?? 'Credit Card';
  }

  get cancelActionLabelDefault() {
    return this.args.cancelActionLabel ?? 'Cancel';
  }

  get showCouponAlert() {
    return this.args.showCouponAlert ?? true;
  }

  get canCancel() {
    return this.args.canCancel ?? false;
  }

  async updateAndSaveAccountAddress() {
    let { addressLine1, addressLine2, city, zipCode, country, state } = this.formValues;
    this.account.current.addressLine1 = addressLine1;
    this.account.current.addressLine2 = addressLine2;
    this.account.current.city = city;
    this.account.current.zipCode = zipCode;
    this.account.current.country = country;
    this.account.current.state = state;

    try {
      await this.account.current.save();
    } catch (e) {
      this.account.current.rollbackAttributes();
      throw e;
    }
  }

  submitTask = task(async (evt: Event) => {
    evt.preventDefault();

    await this.validations.validateModel();

    let setupIntentClientSecret;

    try {
      setupIntentClientSecret = await this.billing.getSetupIntent.perform();
    } catch {
      return this.alerts.error(
        'stripe.setupIntentError',
        'Something went wrong communicating with our billing partner. Please try again.',
      );
    }

    let { name } = this.formValues;
    let { setupIntent, error } = await this.stripe.handleCardSetup(setupIntentClientSecret, this.cardNumberElement, {
      payment_method_data: {
        billing_details: { name, address: { postal_code: this.formValues.zipCode } },
      },
    });

    if (error) {
      return this.alerts.error('stripe.handleCardSetupError', error.message);
    }

    // account billing address is updated in-component: only created Stripe card object/token is sent to "submitAction" for initializing/updating a subscription
    await this.updateAndSaveAccountAddress();
    await this.args.submitAction({ setupIntent });
  });

  @tracked cardNumberElement: any = null;
  @tracked cardExpiryElement: any = null;
  @tracked cardCvcElement: any = null;
  @tracked cardNumberComplete = false;
  @tracked cardExpiryComplete = false;
  @tracked cardCvcComplete = false;
  @tracked cardNumberError = false;
  @tracked cardExpiryError = false;
  @tracked cardCvcError = false;

  @dependentKeyCompat
  get allStripeElementsComplete() {
    return this.cardNumberComplete && this.cardExpiryComplete && this.cardCvcComplete;
  }

  @action
  onStripeInputChange(elementName: string, stripeElement: any, event: Record<string, any>) {
    if (elementName === 'cardNumber') {
      this.cardNumberElement = stripeElement;
      this.cardNumberError = event.error;
      this.cardNumberComplete = event.complete;
    } else if (elementName === 'cardExpiry') {
      this.cardExpiryElement = stripeElement;
      this.cardExpiryError = event.error;
      this.cardExpiryComplete = event.complete;
      // eslint-disable-next-line spellcheck/spell-checker
    } else if (elementName === 'cardCvc') {
      this.cardCvcElement = stripeElement;
      this.cardCvcError = event.error;
      this.cardCvcComplete = event.complete;
    }
  }

  @action
  setAddressFormValue(field: keyof AccountAddressFormValues, value: string) {
    this.formValues = {
      ...this.formValues,
      [field]: value,
    };
  }

  @action
  onNameChange(name: string) {
    this.formValues = {
      ...this.formValues,
      name,
    };
    this.validations.validateAttribute('name');
  }

  get nextBillDate() {
    return moment().add(1, 'month').format('Do MMMM YYYY');
  }

  get isTotalDueVisible() {
    return (this.billing.startDateInFuture === false && this.args.showTotalDue) ?? false;
  }

  get couponCode() {
    return this.args.couponCode ?? '';
  }
}
